import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Avatar,
  Collapse,
  Badge,
  Paper,
  BottomNavigation,
  BottomNavigationAction,
  Typography,
  Menu,
  MenuItem,
  Divider,
  Chip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Payment as PaymentIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  HelpOutline as HelpIcon,
  ExpandLess,
  ExpandMore,
  AutoAwesome as AutoAwesomeIcon,
  Insights as InsightsIcon,
  AccountBalance as AccountBalanceIcon,
  Description as DescriptionIcon,
  Group as GroupIcon,
  Chat as ChatIcon,
  Subscriptions as SubscriptionsIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  AccountCircle as AccountCircleIcon,
  Assessment as ReportIcon,
  AdminPanelSettings as AdminIcon,
  Menu as MenuIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';
import { useAuth } from '../contexts/AuthContext';
import { getTotalUnreadCount } from '../services/messageService';
import DownloadApp from './DownloadApp';
import { Link } from 'react-router-dom';
import { UserRole } from '../types/userTypes';
import { Link as RouterLink } from 'react-router-dom';
import { SxProps, Theme } from '@mui/material/styles';

interface NavigationProps {
  children: React.ReactNode;
}

const drawerWidth = 240;

const Navigation: React.FC<NavigationProps> = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, userProfile, logout, hasPermission, isOwner } = useAuth();
  
  const isSmallMobile = useMediaQuery('(max-width:360px)');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [payrollOpen, setPayrollOpen] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [expandAllOnMobile, setExpandAllOnMobile] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const closeDrawer = () => {
    setDrawerOpen(false);
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  useEffect(() => {
    // Close drawer when route changes on mobile
    if (isMobile) {
      closeDrawer();
    }
    
    // Check if the current path starts with a payroll-related path
    const isPayrollPath = ['/payroll', '/automated-payroll', '/payroll-insights'].some(
      path => location.pathname.startsWith(path)
    );
    
    // Open payroll submenu if we're on a payroll page
    setPayrollOpen(isPayrollPath);
  }, [location.pathname, isMobile]);
  
  // Auto-expand all menu items when drawer opens on mobile
  useEffect(() => {
    if (isMobile && drawerOpen) {
      setExpandAllOnMobile(true);
    }
  }, [isMobile, drawerOpen]);
  
  // Get unread message count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (currentUser) {
        try {
          const result = await getTotalUnreadCount(currentUser.uid);
          if (result.success && typeof result.data === 'number') {
            setUnreadMessageCount(result.data);
          }
        } catch (error) {
          console.error('Error fetching unread message count:', error);
        }
      }
    };
    
    fetchUnreadCount();
    
    // Set up interval to check for new messages
    const intervalId = setInterval(fetchUnreadCount, 60000); // Check every minute
    
    return () => clearInterval(intervalId);
  }, [currentUser]);

  // Handle profile menu
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };
  
  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };
  
  const handleProfileClick = () => {
    navigate('/profile');
    handleProfileMenuClose();
  };
  
  const handleSettingsClick = () => {
    navigate('/settings');
    handleProfileMenuClose();
  };
  
  const handlePayrollClick = () => {
    setPayrollOpen(!payrollOpen);
  };

  const handleMenuItemClick = (item: any) => {
    if (item.path) {
      navigate(item.path);
    }
  };

  // Menu is built per tier from permissions (not raw role strings), so Owner /
  // Company-staff / Employee each see only what they're allowed to.
  //
  // The App Owner gets a dedicated oversight-only menu: companies/users/payroll
  // visibility across the whole platform, with NONE of the employee/social surfaces
  // (My Dashboard, My Tax Info, Friends, Messages, personal Direct Deposit /
  // Subscription) that the shared shell would otherwise show them.
  const ownerMenuItems = [
    { text: 'Owner Console', icon: <AdminIcon />, path: '/owner-dashboard' },
    { text: 'Companies', icon: <BusinessIcon />, path: '/companies' },
    { text: 'Employees', icon: <PeopleIcon />, path: '/employees' },
    { text: 'Time records', icon: <TimeIcon />, path: '/time-records' },
    { text: 'Payroll', icon: <PaymentIcon />, path: '/payroll', isSubmenu: true },
    { text: 'Tax Documents', icon: <DescriptionIcon />, path: '/tax-documents' },
    { text: 'Reporting', icon: <ReportIcon />, path: '/reporting' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    { text: 'Support', icon: <HelpIcon />, path: '/support' },
  ];

  const standardMenuItems = [
    ...(hasPermission('viewReports') ? [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' }
    ] : []),
    { text: 'My Dashboard', icon: <PersonIcon />, path: '/employee-dashboard' },
    { text: 'Time clock', icon: <TimeIcon />, path: '/time-clock' },
    ...(hasPermission('viewEmployees') ? [
      { text: 'Employees', icon: <PeopleIcon />, path: '/employees' }
    ] : []),
    ...(hasPermission('viewEmployees') ? [
      { text: 'Time records', icon: <TimeIcon />, path: '/time-records' }
    ] : []),
    ...(hasPermission('viewPayroll') ? [
      { text: 'Payroll', icon: <PaymentIcon />, path: '/payroll', isSubmenu: true }
    ] : []),
    ...(hasPermission('viewCompanies') ? [
      { text: 'Companies', icon: <BusinessIcon />, path: '/companies' }
    ] : []),
    { text: 'My Tax Info', icon: <DescriptionIcon />, path: '/employee-tax' },
    { text: 'Friends', icon: <GroupIcon />, path: '/friends' },
    {
      text: 'Messages',
      icon: (
        <Badge badgeContent={unreadMessageCount} color="error" max={99}>
          <ChatIcon />
        </Badge>
      ),
      path: '/messages'
    },
    ...(hasPermission('viewPayroll') ? [
      { text: 'Direct Deposit', icon: <AccountBalanceIcon />, path: '/bank-account-setup' }
    ] : []),
    ...(hasPermission('viewReports') ? [
      { text: 'Tax Documents', icon: <DescriptionIcon />, path: '/tax-documents' }
    ] : []),
    ...(hasPermission('manageSettings') ? [
      { text: 'Subscription', icon: <SubscriptionsIcon />, path: '/subscription' }
    ] : []),
    ...(hasPermission('viewSettings') ? [
      { text: 'Settings', icon: <SettingsIcon />, path: '/settings' }
    ] : []),
    { text: 'Support', icon: <HelpIcon />, path: '/support' }
  ];

  const menuItems = isOwner ? ownerMenuItems : standardMenuItems;

  // Create responsive drawer styles
  const getDrawerWidth = () => {
    if (isSmallMobile) return '85vw';
    if (isMobile) return '280px';
    return `${drawerWidth}px`;
  };
  
  const drawer = (
    <Box 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        bgcolor: 'background.paper'
      }}
    >
      <Box 
        onClick={() => navigate('/', { state: { from: '/' } })}
        sx={{ 
          p: 2, 
          borderBottom: 1, 
          borderColor: 'divider',
          textDecoration: 'none',
          color: 'inherit',
          cursor: 'pointer'
        } as SxProps<Theme>}
      >
        <Logo />
      </Box>
      
      <Box sx={{ flex: 1, overflowY: 'auto', pt: 2 }}>
        <List>
          {menuItems.map((item) => (
            <React.Fragment key={item.text}>
              {item.text === 'Payroll' ? (
                <>
                  <ListItem disablePadding sx={{ display: 'block', mb: 0.5 }}>
                    <ListItemButton
                      onClick={handlePayrollClick}
                      sx={{ 
                        py: isMobile ? 1.25 : 1.5,
                        px: 2,
                        borderRadius: 1,
                        transition: theme.transitions.create(['background-color'], {
                          duration: theme.transitions.duration.shorter,
                        }),
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <ListItemIcon 
                        sx={{ 
                          minWidth: isMobile ? 40 : 48,
                          color: 'inherit',
                          transition: theme.transitions.create(['color'], {
                            duration: theme.transitions.duration.shorter,
                          }),
                        }}
                      >
                        <PaymentIcon />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Payroll" 
                        primaryTypographyProps={{ 
                          fontSize: isMobile ? '0.9rem' : '1rem',
                          fontWeight: 400,
                        }}
                      />
                      {payrollOpen ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                  </ListItem>
                  
                  <Collapse 
                    in={payrollOpen} 
                    timeout="auto" 
                    unmountOnExit
                    sx={{
                      '& .MuiCollapse-wrapperInner': {
                        transition: theme.transitions.create(['margin'], {
                          duration: theme.transitions.duration.enteringScreen,
                        }),
                      },
                    }}
                  >
                    <List component="div" disablePadding>
                      {[
                        { text: 'Payday', icon: <PaymentIcon />, path: '/payroll' },
                        { text: 'Pay lists', icon: <AutoAwesomeIcon />, path: '/automated-payroll' },
                        { text: 'Payroll insights', icon: <InsightsIcon />, path: '/payroll-insights' },
                        { text: 'Reporting', icon: <ReportIcon />, path: '/reporting' },
                      ].map((subItem) => (
                        <ListItemButton
                          key={subItem.text}
                          sx={{ 
                            pl: isMobile ? 3 : 4,
                            py: isMobile ? 1.25 : 1.5,
                            my: 0.5,
                            mx: 1,
                            borderRadius: 1,
                            '&.Mui-selected': {
                              bgcolor: 'primary.light',
                              color: 'primary.contrastText',
                              '& .MuiListItemIcon-root': {
                                color: 'primary.contrastText',
                              },
                              '&:hover': {
                                bgcolor: 'primary.main',
                              },
                            },
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                          selected={location.pathname === subItem.path}
                          onClick={() => {
                            navigate(subItem.path);
                            if (isMobile) closeDrawer();
                          }}
                        >
                          <ListItemIcon 
                            sx={{ 
                              minWidth: isMobile ? 40 : 48,
                              color: location.pathname === subItem.path ? 'primary.contrastText' : 'inherit',
                            }}
                          >
                            {subItem.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={subItem.text}
                            primaryTypographyProps={{ 
                              fontSize: isMobile ? '0.9rem' : '1rem',
                              fontWeight: location.pathname === subItem.path ? 500 : 400,
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </>
              ) : (
                <ListItem disablePadding sx={{ display: 'block', mb: 0.5 }}>
                  <ListItemButton
                    onClick={() => handleMenuItemClick(item)}
                    selected={location.pathname === item.path}
                    sx={{
                      py: isMobile ? 1.25 : 1.5,
                      px: 2,
                      borderRadius: 1,
                      transition: theme.transitions.create(['background-color'], {
                        duration: theme.transitions.duration.shorter,
                      }),
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: location.pathname === item.path ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>
      
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        {/* DownloadApp component removed */}
      </Box>
    </Box>
  );

  // Create a simplified menu for bottom navigation. The owner's mobile bar mirrors
  // their oversight menu rather than the employee/social shortcuts.
  const bottomNavItems = isOwner
    ? [
        { text: 'Console', icon: <AdminIcon />, path: '/owner-dashboard' },
        { text: 'Companies', icon: <BusinessIcon />, path: '/companies' },
        { text: 'Employees', icon: <PeopleIcon />, path: '/employees' },
        { text: 'Time', icon: <TimeIcon />, path: '/time-records' },
        { text: 'Payroll', icon: <PaymentIcon />, path: '/payroll' },
      ]
    : [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
        { text: 'My Dashboard', icon: <PersonIcon />, path: '/employee-dashboard' },
        { text: 'Time clock', icon: <TimeIcon />, path: '/time-clock' },
        { text: 'Messages', icon: <ChatIcon />, path: '/messages' },
      ];

  // Avatar display info
  const userFirstName = userProfile?.firstName || currentUser?.email?.split('@')[0] || 'User';
  const userInitial = (userProfile?.firstName?.charAt(0) || currentUser?.email?.charAt(0) || 'U').toUpperCase();
  const profilePhotoURL = userProfile?.photoURL;

  // Add user menu handlers
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogoutClick = async () => {
    try {
      await logout();
      navigate('/');
      handleCloseUserMenu();
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  if (!currentUser) {
    return <>{children}</>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* App bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
          boxShadow: 1,
          pt: { xs: '10px', sm: 0 },
        }}
      >
        <Toolbar>
          {/* Mobile view - 3 sections: menu button, centered logo, avatar */}
          <Box sx={{ 
            display: 'flex', 
            width: '100%', 
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            {/* Left side - Menu button */}
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={toggleDrawer}
              sx={{ 
                display: { sm: 'none' },
                color: 'primary.main',
                bgcolor: 'primary.lighter',
                '&:hover': {
                  bgcolor: 'primary.light',
                },
                width: 42,
                height: 42
              }}
            >
              <MenuIcon />
            </IconButton>
            
            {/* Center - Logo for mobile */}
            <Box
              sx={{
                display: { xs: 'flex', sm: 'none' },
                justifyContent: 'center',
                alignItems: 'center',
                py: 1.5
              }}
            >
              <Box 
                onClick={() => navigate('/', { state: { from: '/' } })}
                sx={{ 
                  color: 'primary.main', 
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer'
                }}
              >
                <Logo variant="full" size="small" />
              </Box>
            </Box>
            
            {/* Desktop - Left side page title */}
            <Box sx={{ 
              flexGrow: 1, 
              display: { xs: 'none', sm: 'flex' }, 
              alignItems: 'center' 
            }}>
              <Box 
                onClick={() => navigate('/', { state: { from: '/' } })}
                sx={{ 
                  color: 'primary.main', 
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  mr: 2,
                  cursor: 'pointer'
                }}
              >
                <Logo variant="icon" size="small" />
                <Box sx={{ ml: 0.5, fontWeight: 'bold', fontSize: '1.2rem' }}>
                  Superior
                </Box>
              </Box>
              
              <Box sx={{ fontWeight: 'medium', fontSize: '1.1rem' }}>
                {/* Add page title logic here */}
              </Box>
            </Box>
            
            {/* Right side - User greeting and avatar on right */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!isMobile && (
                <Typography variant="body1" sx={{ display: { xs: 'none', md: 'block' } }}>
                  Hey there, {userFirstName}!
                </Typography>
              )}
              {isMobile && (
                <Typography variant="body2" sx={{ 
                  fontWeight: 500,
                  whiteSpace: 'nowrap'
                }}>
                  Hey there, {userFirstName}!
                </Typography>
              )}
              <Box>
                <IconButton
                  onClick={handleOpenUserMenu}
                  sx={{ 
                    p: 0,
                    '&:hover': {
                      bgcolor: 'transparent'
                    }
                  }}
                >
                  <Avatar 
                    src={profilePhotoURL} 
                    alt="Profile" 
                    sx={{ 
                      width: isMobile ? 38 : 42, 
                      height: isMobile ? 38 : 42,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      border: '2px solid',
                      borderColor: 'primary.light',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.5)',
                        transform: 'scale(1.05)',
                        transition: 'all 0.2s ease-in-out'
                      }
                    }}
                  >
                    {userInitial}
                  </Avatar>
                </IconButton>
              </Box>
              
              {/* User dropdown menu */}
              <Menu
                sx={{ mt: '45px' }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                {/* The employee social profile is irrelevant to the platform owner. */}
                {!isOwner && (
                  <MenuItem onClick={handleProfileClick}>
                    <ListItemIcon>
                      <AccountCircleIcon fontSize="small" />
                    </ListItemIcon>
                    Profile
                  </MenuItem>
                )}
                <MenuItem onClick={() => {
                  navigate('/settings');
                  handleCloseUserMenu();
                }}>
                  <ListItemIcon>
                    <SettingsIcon fontSize="small" />
                  </ListItemIcon>
                  Settings
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogoutClick}>
                  <ListItemIcon>
                    <LogoutIcon fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          },
        }}
        open
      >
        {drawer}
      </Drawer>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={closeDrawer}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: getDrawerWidth(),
            bgcolor: 'background.paper',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          mt: { xs: 8, sm: 8 },
        }}
      >
        {children}
      </Box>

      {/* Bottom navigation for mobile */}
      {isMobile && (
        <Paper 
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0,
            borderTop: 1,
            borderColor: 'divider',
            zIndex: theme.zIndex.appBar,
          }} 
          elevation={3}
        >
          <BottomNavigation
            value={location.pathname}
            onChange={(event, newValue) => {
              navigate(newValue);
            }}
            sx={{
              bgcolor: 'background.paper',
              height: 'auto',
              py: 1,
            }}
          >
            {bottomNavItems.map((item) => (
              <BottomNavigationAction
                key={item.text}
                label={item.text}
                icon={item.icon}
                value={item.path}
                sx={{
                  minWidth: 'auto',
                  px: 1,
                  '& .MuiBottomNavigationAction-label': {
                    fontSize: '0.675rem',
                  },
                }}
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
};

export default Navigation;